import { EChartsOption } from 'echarts'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getSalesMetrics } from '@/api/clarity'
import { ScalrTableColumn } from '@/components/customComponents'
import {
  pieChartOptions,
  stackedBarChartOptions,
} from '@/constants/CHART_CONSTANTS'
import {
  ItemCountOrderedMixModel,
  MetricCardComparisonModel,
  SalesChartsData,
  SalesMetricsModel,
} from '@/types/clarity'
import { dateToString } from '@/utils'
import { DateType, getDateRange, getTrendingDate } from '@/utils/date'

const itemCountMixColumns: ScalrTableColumn[] = [
  { header: 'Item', accessorKey: 'itemName', type: 'text' },
  { header: 'Mix %', accessorKey: 'itemMixPercentage', type: 'percentage' },
]

const itemCountOrderedMixColumns: ScalrTableColumn[] = [
  { header: 'Item', accessorKey: 'itemName', type: 'text' },
  { header: 'Uplift', accessorKey: 'uplift', type: 'number' },
  { header: 'Mix %', accessorKey: 'itemMixPercentage', type: 'percentage' },
]

const modifierCountMixColumns: ScalrTableColumn[] = [
  { header: 'Modifier', accessorKey: 'modifier', type: 'text' },
  {
    header: 'Order Penetration',
    accessorKey: 'orderPenetration',
    type: 'percentage',
  },
]

// Helper function to transform category count mix data into pie chart options
const transformCategoryCountMixToChart = (
  data?: { name: string; value: string }[]
): EChartsOption | undefined => {
  if (!data || data.length === 0) return undefined

  const seriesNames = data.map((item) => item.name)
  const rawData = data.map((item) => parseFloat(item.value))

  return pieChartOptions(rawData, seriesNames, 'percentage')
}

// Helper function to transform sales in a week data into stacked bar chart options
const transformSalesInAWeekToChart = (data?: {
  [period: string]: number[]
}): EChartsOption | undefined => {
  if (!data || Object.keys(data).length === 0) return undefined

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const seriesNames = Object.keys(data)
  const rawData = seriesNames.map((period) => data[period])

  return stackedBarChartOptions(rawData, seriesNames, dayNames, '60%', 'money')
}

export default function useSalesMetrics(
  startDate: string,
  endDate: string,
  dateFilterType: DateType,
  branches: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [companyData, setCompanyData] = useState<SalesMetricsModel>()
  const [metricCardData, setMetricCardData] =
    useState<MetricCardComparisonModel<SalesMetricsModel>>()
  const [chartData, setChartData] = useState<SalesChartsData | null>()

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { salesMetrics, salesChartsData } = await getSalesMetrics(
          branches,
          startDate,
          endDate
        )
        setLocalTrendingData(branches, setMetricCardData)

        setCompanyData(salesMetrics)
        return {
          ...salesChartsData,
          // Add id field to itemCountMixData if it doesn't exist
          itemCountMixData: salesChartsData.itemCountMixData?.map((item) => ({
            ...item,
            id: item.id || item.itemName,
          })),
          // Transform chart data
          categoryCountMixOptions: transformCategoryCountMixToChart(
            salesChartsData.categoryCountMixOptions
          ),
          salesInAWeekOptions: transformSalesInAWeekToChart(
            salesChartsData.salesInAWeekOptions
          ),
          itemCountMixColumns,
          itemCountOrderedMixColumns,
          modifierCountMixColumns,
        }
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [startDate, endDate, branches.toString()])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange

        const { salesChartsData } = await getSalesMetrics(
          branches,
          dateToString(trendingDate, 'from'),
          dateToString(trendingDate, 'to')
        )

        return {
          ...salesChartsData,
          // Add id field to itemCountMixData if it doesn't exist
          itemCountMixData: salesChartsData.itemCountMixData?.map((item) => ({
            ...item,
            id: item.id || item.itemName,
          })),
          // Transform chart data
          categoryCountMixOptions: transformCategoryCountMixToChart(
            salesChartsData.categoryCountMixOptions
          ),
          salesInAWeekOptions: transformSalesInAWeekToChart(
            salesChartsData.salesInAWeekOptions
          ),
          itemCountMixColumns,
          itemCountOrderedMixColumns,
          modifierCountMixColumns,
        }
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate, dateFilterType])

  const fetchAllData = useCallback(async () => {
    // Guard against empty dates (prevents API calls during initialization)
    if (!startDate || !endDate) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Execute all fetch operations in parallel
      const [newChartData, newTrendChartData] = await Promise.all([
        fetchData(),
        fetchTrendData(),
      ])

      if (!newChartData) {
        throw new Error('Failed to fetch sales data!')
      }

      if (newTrendChartData) {
        const itemCountOrderedMixData = setUpliftOrderedMixData(
          newChartData,
          newTrendChartData
        )
        setChartData({ ...newChartData, itemCountOrderedMixData })
      } else {
        setChartData(newChartData)
      }
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData, fetchTrendData])

  const refetch = useCallback(() => {
    console.log('Refetching sales data...')
    return fetchAllData()
  }, [fetchAllData])

  // Initial fetch on mount or when dependencies change
  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  const setUpliftOrderedMixData = (
    chartData: SalesChartsData,
    trendingChartData: SalesChartsData
  ) => {
    const currentItemMixData = chartData?.itemCountMixData
    const prevItemMixData = trendingChartData?.itemCountMixData
    const newList: ItemCountOrderedMixModel[] = []
    currentItemMixData?.forEach((item) => {
      const prevItem = prevItemMixData?.find(
        (prevItem) => prevItem.itemName === item.itemName
      )
      if (prevItem) {
        const uplift = item.itemCount - prevItem.itemCount
        newList.push({
          ...item,
          id: item.itemName,
          uplift,
        })
      }
    })
    const sorted = newList.sort((a, b) => b.uplift - a.uplift)
    return sorted.slice(0, 5)
  }

  return {
    chartData: {
      ...chartData,
      itemCountMixColumns,
      itemCountOrderedMixColumns,
      modifierCountMixColumns,
    },
    companyData,
    metricCardData,
    loading,
    error,
    refetch,
  }
}

async function setLocalTrendingData(
  branches: string,
  setMetricCardData: (
    data: MetricCardComparisonModel<SalesMetricsModel>
  ) => void
) {
  const mtdDate = getDateRange('month-to-date')
  const ytdDate = getDateRange('year-to-date')
  const mtdTrendingDate = mtdDate.trending as DateRange
  const ytdTrendingDate = ytdDate.trending as DateRange

  const getTdMetric = async (args: [string, string]) => {
    const { salesMetrics } = await getSalesMetrics(branches, ...args)
    return salesMetrics
  }

  const [
    mCompanyMetrics,
    mCompanyMetricsTrending,
    yCompanyMetrics,
    yCompanyMetricsTrending,
  ] = await Promise.all([
    getTdMetric([dateToString(mtdDate, 'from'), dateToString(mtdDate, 'to')]),
    getTdMetric([
      dateToString(mtdTrendingDate, 'from'),
      dateToString(mtdTrendingDate, 'to'),
    ]),
    getTdMetric([dateToString(ytdDate, 'from'), dateToString(ytdDate, 'to')]),
    getTdMetric([
      dateToString(ytdTrendingDate, 'from'),
      dateToString(ytdTrendingDate, 'to'),
    ]),
  ])

  setMetricCardData({
    currMetricsMonthly: mCompanyMetrics,
    currMetricsYearly: yCompanyMetrics,
    prevMetricsMonthly: mCompanyMetricsTrending,
    prevMetricsYearly: yCompanyMetricsTrending,
  })
}
